/**
 * Accounting desk — expenses, expense categories, employees, advances and salary
 * payments. Persisted in localStorage (offline-first) and mirrored to the backend
 * (/api/accounting) like the consultancy bundle.
 *
 * Payroll side-effects follow the Nourishya flow:
 *  - giving an advance increments the employee's outstanding advance AND logs a
 *    "Salary / Advance" expense.
 *  - paying salary deducts outstanding advances and logs a "Salary / Monthly Salary"
 *    expense for the net cash paid.
 */
import { useCallback, useEffect, useState } from 'react';
import { loadDevJson, saveDevJson } from './devStorage';
import { accountingApi, hasAuthToken } from '../api';
import { adminEntryIso } from './workspaceAsOf';
import { handleSyncRejection } from './periodLockNotice';

export const ACCOUNTING_KEY = 'accounting_bundle_v1';
export const ACCOUNTING_DATA_EVENT = 'accounting-data';

function emit() {
  window.dispatchEvent(new Event(ACCOUNTING_DATA_EVENT));
}

function uid(prefix: string): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export type PaymentMethod = 'cash' | 'upi' | 'bank';

export type ExpenseCategory = {
  id: string;
  name: string;
  expenseType: 'one_time' | 'recurring';
  color?: string;
  createdAt: string;
};

export type Expense = {
  id: string;
  category: string;
  subCategory?: string;
  description?: string;
  amount: number;
  expenseDate: string;
  paymentMethod: PaymentMethod | '';
  paidBy?: string;
  vendor?: string;
  referenceType?: string | null;
  referenceId?: string | null;
  metadata?: Record<string, unknown>;
  createdAt: string;
};

export type Employee = {
  id: string;
  employeeCode: string;
  name: string;
  mobile?: string;
  email?: string;
  designation?: string;
  joiningDate: string;
  salaryPerMonth: number;
  address?: string;
  advancesTaken: number;
  isActive: boolean;
  createdAt: string;
};

export type EmployeeAdvance = {
  id: string;
  employeeId: string;
  employeeName: string;
  amount: number;
  purpose?: string;
  advanceDate: string;
  paymentMethod: PaymentMethod;
  paidBy?: string;
  createdAt: string;
};

export type SalaryPayment = {
  id: string;
  employeeId: string;
  employeeName: string;
  month: string;
  year: number;
  basicSalary: number;
  bonus: number;
  advancesDeducted: number;
  otherDeductions: number;
  netPay: number;
  paymentMethod: PaymentMethod;
  paidBy?: string;
  paymentDate: string;
  createdAt: string;
};

export type AccountingBundle = {
  categories: ExpenseCategory[];
  expenses: Expense[];
  employees: Employee[];
  advances: EmployeeAdvance[];
  salaryPayments: SalaryPayment[];
};

export function readAccountingBundle(): AccountingBundle {
  const raw = loadDevJson<Partial<AccountingBundle> | null>(ACCOUNTING_KEY, null);
  return {
    categories: Array.isArray(raw?.categories) ? (raw!.categories as ExpenseCategory[]) : [],
    expenses: Array.isArray(raw?.expenses) ? (raw!.expenses as Expense[]) : [],
    employees: Array.isArray(raw?.employees) ? (raw!.employees as Employee[]) : [],
    advances: Array.isArray(raw?.advances) ? (raw!.advances as EmployeeAdvance[]) : [],
    salaryPayments: Array.isArray(raw?.salaryPayments) ? (raw!.salaryPayments as SalaryPayment[]) : [],
  };
}

export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export function emptyAccountingBundle(): AccountingBundle {
  return { categories: [], expenses: [], employees: [], advances: [], salaryPayments: [] };
}

/** Create a category on demand (e.g. payroll logging) if it does not exist yet. */
function ensureCategory(bundle: AccountingBundle, name: string): AccountingBundle {
  if (bundle.categories.some((c) => c.name.toLowerCase() === name.toLowerCase())) return bundle;
  const category: ExpenseCategory = {
    id: uid('cat'),
    name,
    expenseType: 'one_time',
    createdAt: new Date().toISOString(),
  };
  return { ...bundle, categories: [...bundle.categories, category] };
}

export function writeAccountingBundle(next: AccountingBundle): void {
  saveDevJson(ACCOUNTING_KEY, next);
  if (hasAuthToken()) {
    accountingApi.put(next as unknown as Record<string, unknown>).catch((e) => {
      // Closed-year rejections restore the server copy; otherwise keep local and retry on next change.
      handleSyncRejection(e);
    });
  }
  emit();
}

export function useAccountingBundle(): readonly [
  AccountingBundle,
  (fn: (prev: AccountingBundle) => AccountingBundle) => void
] {
  const [bundle, setBundle] = useState(() => readAccountingBundle());

  useEffect(() => {
    const on = () => setBundle(readAccountingBundle());
    window.addEventListener(ACCOUNTING_DATA_EVENT, on);
    return () => window.removeEventListener(ACCOUNTING_DATA_EVENT, on);
  }, []);

  const update = useCallback((fn: (prev: AccountingBundle) => AccountingBundle) => {
    const next = fn(readAccountingBundle());
    writeAccountingBundle(next);
    setBundle(next);
  }, []);

  return [bundle, update] as const;
}

/* --------------------------------- Selectors --------------------------------- */

export function salaryBalance(emp: Employee): number {
  return Math.max(0, (emp.salaryPerMonth || 0) - (emp.advancesTaken || 0));
}

export function nextEmployeeCode(employees: Employee[]): string {
  return `EMP${String(employees.length + 1).padStart(3, '0')}`;
}

/* -------------------------------- Categories -------------------------------- */

export function addCategory(
  bundle: AccountingBundle,
  input: { name: string; expenseType?: 'one_time' | 'recurring'; color?: string }
): AccountingBundle {
  const name = input.name.trim();
  if (!name) return bundle;
  if (bundle.categories.some((c) => c.name.toLowerCase() === name.toLowerCase())) return bundle;
  const cat: ExpenseCategory = {
    id: uid('cat'),
    name,
    expenseType: input.expenseType || 'one_time',
    color: input.color,
    createdAt: new Date().toISOString(),
  };
  return { ...bundle, categories: [...bundle.categories, cat] };
}

export function deleteCategory(bundle: AccountingBundle, id: string): AccountingBundle {
  return { ...bundle, categories: bundle.categories.filter((c) => c.id !== id) };
}

/* --------------------------------- Expenses --------------------------------- */

export function addExpense(
  bundle: AccountingBundle,
  input: {
    category: string;
    subCategory?: string;
    description?: string;
    amount: number;
    expenseDate?: string;
    paymentMethod?: PaymentMethod | '';
    paidBy?: string;
    vendor?: string;
  }
): AccountingBundle {
  const expense: Expense = {
    id: uid('exp'),
    category: input.category,
    subCategory: input.subCategory || '',
    description: input.description || '',
    amount: Math.max(0, Number(input.amount) || 0),
    expenseDate: input.expenseDate || adminEntryIso(),
    paymentMethod: input.paymentMethod ?? 'cash',
    paidBy: input.paidBy || '',
    vendor: input.vendor || '',
    referenceType: null,
    referenceId: null,
    metadata: {},
    createdAt: new Date().toISOString(),
  };
  return { ...bundle, expenses: [expense, ...bundle.expenses] };
}

export function deleteExpense(bundle: AccountingBundle, id: string): AccountingBundle {
  return { ...bundle, expenses: bundle.expenses.filter((e) => e.id !== id) };
}

/* -------------------------------- Employees -------------------------------- */

export function addEmployee(
  bundle: AccountingBundle,
  input: {
    name: string;
    mobile?: string;
    email?: string;
    designation?: string;
    joiningDate?: string;
    salaryPerMonth?: number;
    address?: string;
  }
): AccountingBundle {
  const name = input.name.trim();
  if (!name) return bundle;
  const emp: Employee = {
    id: uid('emp'),
    employeeCode: nextEmployeeCode(bundle.employees),
    name,
    mobile: input.mobile || '',
    email: input.email || '',
    designation: input.designation || '',
    joiningDate: input.joiningDate || adminEntryIso(),
    salaryPerMonth: Math.max(0, Number(input.salaryPerMonth) || 0),
    address: input.address || '',
    advancesTaken: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
  };
  return { ...bundle, employees: [emp, ...bundle.employees] };
}

export function updateEmployee(bundle: AccountingBundle, id: string, patch: Partial<Employee>): AccountingBundle {
  return {
    ...bundle,
    employees: bundle.employees.map((e) => (e.id === id ? { ...e, ...patch } : e)),
  };
}

export function deleteEmployee(bundle: AccountingBundle, id: string): AccountingBundle {
  return { ...bundle, employees: bundle.employees.filter((e) => e.id !== id) };
}

/* -------------------------- Advances & salary (with side-effects) -------------------------- */

export function giveAdvance(
  bundle: AccountingBundle,
  input: {
    employeeId: string;
    amount: number;
    purpose?: string;
    advanceDate?: string;
    paymentMethod?: PaymentMethod;
    paidBy?: string;
  }
): AccountingBundle {
  const emp = bundle.employees.find((e) => e.id === input.employeeId);
  const amount = Math.max(0, Number(input.amount) || 0);
  if (!emp || amount <= 0) return bundle;

  const advanceDate = input.advanceDate || adminEntryIso();
  const method: PaymentMethod = input.paymentMethod || 'cash';
  const advance: EmployeeAdvance = {
    id: uid('adv'),
    employeeId: emp.id,
    employeeName: emp.name,
    amount,
    purpose: input.purpose || '',
    advanceDate,
    paymentMethod: method,
    paidBy: input.paidBy || '',
    createdAt: new Date().toISOString(),
  };

  const expense: Expense = {
    id: uid('exp'),
    category: 'Salary',
    subCategory: 'Advance',
    description: `Advance to ${emp.name}${input.purpose ? ` — ${input.purpose}` : ''}`,
    amount,
    expenseDate: advanceDate,
    paymentMethod: method,
    paidBy: input.paidBy || '',
    referenceType: 'employee_advance',
    referenceId: advance.id,
    metadata: { employeeId: emp.id, employeeName: emp.name },
    createdAt: new Date().toISOString(),
  };

  const withCategory = ensureCategory(bundle, 'Salary');

  return {
    ...withCategory,
    advances: [advance, ...withCategory.advances],
    expenses: [expense, ...withCategory.expenses],
    employees: withCategory.employees.map((e) =>
      e.id === emp.id ? { ...e, advancesTaken: (e.advancesTaken || 0) + amount } : e
    ),
  };
}

export function paySalary(
  bundle: AccountingBundle,
  input: {
    employeeId: string;
    month: string;
    year: number;
    bonus?: number;
    otherDeductions?: number;
    advancesDeducted?: number;
    paymentMethod?: PaymentMethod;
    paidBy?: string;
    paymentDate?: string;
  }
): { bundle: AccountingBundle; error?: string } {
  const emp = bundle.employees.find((e) => e.id === input.employeeId);
  if (!emp) return { bundle, error: 'Employee not found.' };
  if (!emp.isActive) return { bundle, error: 'Employee is inactive.' };

  const already = bundle.salaryPayments.some(
    (p) => p.employeeId === emp.id && p.month === input.month && p.year === input.year
  );
  if (already) return { bundle, error: `Salary for ${input.month} ${input.year} already recorded.` };

  const basicSalary = emp.salaryPerMonth || 0;
  const bonus = Math.max(0, Number(input.bonus) || 0);
  const otherDeductions = Math.max(0, Number(input.otherDeductions) || 0);
  const advancesDeducted = Math.min(
    emp.advancesTaken || 0,
    Math.max(0, Number(input.advancesDeducted ?? emp.advancesTaken) || 0)
  );
  const netPay = Math.max(0, basicSalary + bonus - advancesDeducted - otherDeductions);
  const paymentDate = input.paymentDate || adminEntryIso();
  const method: PaymentMethod = input.paymentMethod || 'cash';

  const payment: SalaryPayment = {
    id: uid('sal'),
    employeeId: emp.id,
    employeeName: emp.name,
    month: input.month,
    year: input.year,
    basicSalary,
    bonus,
    advancesDeducted,
    otherDeductions,
    netPay,
    paymentMethod: method,
    paidBy: input.paidBy || '',
    paymentDate,
    createdAt: new Date().toISOString(),
  };

  const expense: Expense = {
    id: uid('exp'),
    category: 'Salary',
    subCategory: 'Monthly Salary',
    description: `Salary ${input.month} ${input.year} — ${emp.name}`,
    amount: netPay,
    expenseDate: paymentDate,
    paymentMethod: method,
    paidBy: input.paidBy || '',
    referenceType: 'salary_payment',
    referenceId: payment.id,
    metadata: { employeeId: emp.id, employeeName: emp.name, month: input.month, year: input.year },
    createdAt: new Date().toISOString(),
  };

  const withCategory = ensureCategory(bundle, 'Salary');

  return {
    bundle: {
      ...withCategory,
      salaryPayments: [payment, ...withCategory.salaryPayments],
      expenses: [expense, ...withCategory.expenses],
      employees: withCategory.employees.map((e) =>
        e.id === emp.id ? { ...e, advancesTaken: Math.max(0, (e.advancesTaken || 0) - advancesDeducted) } : e
      ),
    },
  };
}
